import { memo, useEffect, useRef } from 'react'
import WaveSurfer from 'wavesurfer.js'
import { useDispatch, useSelector } from 'react-redux'
import { IAudioItem } from 'types/audio'
import * as Styled from './styled'
import { AUDIO_ITEM_COLORS } from '../consts'
import { COLORS } from 'consts/colors'
import { useTimelineItemBodyRect } from 'hooks/timeline'
import { TRootState } from 'store/types'
import { projectDataSelectors } from 'store/features/projectData/selectors'
import projectDataSlice from 'store/features/projectData/slice'

interface IAudioWaveProps {
  audioItem: IAudioItem
  isActive: boolean
  isDraft: boolean
}

const AudioWave = ({ audioItem, isActive, isDraft }: IAudioWaveProps) => {
  const { mediaPath, type, assetId } = audioItem
  const containerRef = useRef<HTMLDivElement>(null)
  const waveSurferRef = useRef<WaveSurfer>(null)
  const dispatch = useDispatch()

  const { waveColor } = AUDIO_ITEM_COLORS[type]

  const cachedMeta = useSelector((state: TRootState) =>
    projectDataSelectors.soundMetaDataByAssetId(state, assetId)
  )
  const cachedPeaks = cachedMeta?.peaks
  const cachedDuration = cachedMeta?.duration

  const { bodyWidth: audioWaveWidth, bodyLeft: audioWaveLeft } =
    useTimelineItemBodyRect({
      trimStart: audioItem.mediaStart,
      originalDuration: audioItem.mediaDuration
    })

  useEffect(() => {
    if (!containerRef.current) return
    const hasCache = Boolean(cachedPeaks && cachedDuration)
    const ws = WaveSurfer.create({
      container: containerRef.current,
      url: hasCache ? undefined : `${mediaPath}`,
      peaks: cachedPeaks?.map(ch => Float32Array.from(ch)),
      duration: hasCache ? cachedDuration : undefined,
      progressColor: waveColor,
      waveColor,
      barWidth: 1,
      barGap: 1,
      cursorWidth: 0,
      barRadius: 1,
      height: 28,
      interact: false
    })
    waveSurferRef.current = ws

    let unsubscribe: (() => void) | undefined
    if (!hasCache && cachedMeta) {
      unsubscribe = ws.on('ready', () => {
        const peaks = ws.exportPeaks({ maxLength: 4000, precision: 1000 })
        dispatch(projectDataSlice.actions.setSoundPeaks({ assetId, peaks }))
      })
    }

    return () => {
      unsubscribe?.()
      ws.destroy()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaPath, containerRef])

  // change wave color when active
  useEffect(() => {
    const color = isActive
      ? COLORS.white
      : isDraft
        ? COLORS.secondary
        : waveColor

    waveSurferRef.current?.setOptions({
      progressColor: color,
      waveColor: color
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, isDraft])

  const audioWaveStyle = {
    width: `${audioWaveWidth}px`,
    left: `${audioWaveLeft}px`
  }

  return <Styled.AudioWaveWrapper ref={containerRef} style={audioWaveStyle} />
}

export default memo(AudioWave)
